import { Injectable, inject, Signal } from '@angular/core';
import {
  injectQuery,
  injectMutation,
  QueryClient,
  CreateQueryResult,
  CreateMutationResult,
} from '@tanstack/angular-query-experimental';
import { lastValueFrom } from 'rxjs';

import { ContentSettingsHttpService } from '@http-services/ContentSettingsHttpService';
import { GetListRequest, GetDetailRequest } from '@rest/ViewSetHttpService';
import { contentSettingsKeys } from '@query/contentSettingsQueryService/content-settings-keys';
import {
  ContentSettings,
  ContentSettingsList,
  CreateContentSettingsDto,
  UpdateContentSettingsPayload,
} from '@query/contentSettingsQueryService/content-settings-types';
import { createSafeParams } from '@query/utils/angular-query.utils';

/**
 * Angular Query wrapper for ContentSettingsHttpService
 * Provides caching, state management, and automatic query invalidation
 */
@Injectable({
  providedIn: 'root',
})
export class ContentSettingsQueryService {
  private readonly contentSettingsService = inject(ContentSettingsHttpService);
  private readonly queryClient = inject(QueryClient);

  /**
   * List content settings with Angular Query
   * @param params Query parameters
   * @param enabled Optional signal to control when the query should be enabled
   * @returns Query result with loading, error, and data states
   */
  getContentSettings({
    params = {},
    enabled,
  }: {
    params?: GetListRequest;
    enabled?: Signal<boolean>;
  } = {}): CreateQueryResult<ContentSettingsList, Error> {
    return injectQuery(() => {
      const isEnabled = enabled ? enabled() : true;
      const safeParams = createSafeParams(params);

      return {
        queryKey: contentSettingsKeys.list(safeParams),
        queryFn: () =>
          lastValueFrom(
            this.contentSettingsService.list(safeParams)
          ) as Promise<ContentSettingsList>,
        enabled: isEnabled,
        staleTime: 30 * 60 * 1000,
      };
    });
  }

  /**
   * Get content settings detail
   * @param uuid Signal containing the content settings UUID
   * @param params Query parameters
   * @param enabled Optional signal to control when the query should be enabled
   * @returns Query result with loading, error, and data states
   */
  getContentSettingsDetail({
    uuid,
    params = {},
    enabled,
  }: {
    uuid: Signal<string | undefined>;
    params?: GetDetailRequest;
    enabled?: Signal<boolean>;
  }): CreateQueryResult<ContentSettings, Error> {
    return injectQuery(() => {
      const settingsUuid = uuid();
      const isEnabled = enabled ? enabled() : true;
      const shouldRun = isEnabled && !!settingsUuid;
      const safeParams = createSafeParams(params);

      return {
        queryKey: contentSettingsKeys.detail(settingsUuid || '', params),
        queryFn: () =>
          lastValueFrom(
            this.contentSettingsService.detail(settingsUuid!, safeParams)
          ) as Promise<ContentSettings>,
        enabled: shouldRun,
        staleTime: 30 * 60 * 1000,
      };
    });
  }

  /**
   * Create new content settings
   * @returns Mutation result with mutate function
   */
  createContentSettings(): CreateMutationResult<ContentSettings, Error, CreateContentSettingsDto> {
    return injectMutation(() => ({
      mutationFn: data => {
        return lastValueFrom(this.contentSettingsService.create(data)) as Promise<ContentSettings>;
      },
      onSuccess: () => {
        return this.queryClient.invalidateQueries({
          queryKey: contentSettingsKeys.invalidateLists(),
        });
      },
    }));
  }

  /**
   * Update existing content settings
   * @returns Mutation result with mutate function
   */
  updateContentSettings(): CreateMutationResult<
    ContentSettings,
    Error,
    UpdateContentSettingsPayload
  > {
    return injectMutation(() => ({
      mutationFn: ({ uuid, data }) => {
        return lastValueFrom(this.contentSettingsService.update(uuid, data));
      },
      onSuccess: (_, variables) => {
        return Promise.all([
          this.queryClient.invalidateQueries({
            queryKey: contentSettingsKeys.detailById(variables.uuid),
          }),
          this.queryClient.invalidateQueries({ queryKey: contentSettingsKeys.lists() }),
        ]);
      },
    }));
  }

  /**
   * Delete content settings
   * @returns Mutation result with mutate function
   */
  deleteContentSettings(): CreateMutationResult<ContentSettings, Error, string> {
    return injectMutation(() => ({
      mutationFn: (uuid: string) => lastValueFrom(this.contentSettingsService.delete(uuid)),
      onSuccess: () => {
        return this.queryClient.invalidateQueries({
          queryKey: contentSettingsKeys.invalidateAll(),
        });
      },
    }));
  }
}
